/**
 * Módulo de armazenamento seguro para tokens de autenticação.
 *
 * Usa sessionStorage em vez de localStorage para:
 * - Reduzir superfície de ataque XSS (dados não persistem entre sessões)
 * - Forçar re-autenticação ao fechar o navegador
 * - Isolar sessões entre abas (opcional)
 *
 * Para aplicações que precisam de persistência, considere:
 * - HttpOnly cookies (mais seguro, requer backend)
 * - Refresh token em httpOnly cookie + access token em memória
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
} as const;

// Configuração: usar sessionStorage (mais seguro) ou localStorage (persiste)
// NOTA: Mudado para localStorage para suportar testes E2E (Playwright storageState)
// Para produção com segurança máxima, considerar: HttpOnly cookies + access token em memória
const USE_SESSION_STORAGE = false;

const storage = USE_SESSION_STORAGE ? sessionStorage : localStorage;

/**
 * Armazena token de acesso de forma segura.
 */
export function setAccessToken(token: string): void {
  storage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
}

/**
 * Obtém token de acesso.
 */
export function getAccessToken(): string | null {
  return storage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

/**
 * Armazena refresh token de forma segura.
 */
export function setRefreshToken(token: string): void {
  storage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
}

/**
 * Obtém refresh token.
 */
export function getRefreshToken(): string | null {
  return storage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Armazena dados do usuário.
 */
export function setUserData(userData: unknown): void {
  storage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
}

/**
 * Obtém dados do usuário.
 */
export function getUserData<T>(): T | null {
  const data = storage.getItem(TOKEN_KEYS.USER_DATA);
  if (!data) return null;
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}

/**
 * Limpa todos os tokens de autenticação.
 */
export function clearAuthTokens(): void {
  storage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  storage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  storage.removeItem(TOKEN_KEYS.USER_DATA);
}

/**
 * Verifica se há tokens armazenados.
 */
export function hasAuthTokens(): boolean {
  return !!getAccessToken();
}

/**
 * Armazena tokens de autenticação.
 */
export function setAuthTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
}

// Meu ativaCare (Portal) - armazenamento separado
const PORTAL_KEYS = {
  TOKEN: 'portalToken',
  PATIENT_ID: 'portalPatientId',
  PATIENT_NAME: 'portalPatientName',
  EXPIRES_AT: 'portalExpiresAt',
} as const;

export const portalStorage = {
  setToken(token: string): void {
    storage.setItem(PORTAL_KEYS.TOKEN, token);
  },

  getToken(): string | null {
    return storage.getItem(PORTAL_KEYS.TOKEN);
  },

  setPatientInfo(patientId: string, patientName: string, expiresAt: string): void {
    storage.setItem(PORTAL_KEYS.PATIENT_ID, patientId);
    storage.setItem(PORTAL_KEYS.PATIENT_NAME, patientName);
    storage.setItem(PORTAL_KEYS.EXPIRES_AT, expiresAt);
  },

  getPatientId(): string | null {
    return storage.getItem(PORTAL_KEYS.PATIENT_ID);
  },

  getPatientName(): string | null {
    return storage.getItem(PORTAL_KEYS.PATIENT_NAME);
  },

  getExpiresAt(): string | null {
    return storage.getItem(PORTAL_KEYS.EXPIRES_AT);
  },

  isExpired(): boolean {
    const expiresAt = storage.getItem(PORTAL_KEYS.EXPIRES_AT);
    if (!expiresAt) return true;
    return new Date(expiresAt) < new Date();
  },

  clear(): void {
    storage.removeItem(PORTAL_KEYS.TOKEN);
    storage.removeItem(PORTAL_KEYS.PATIENT_ID);
    storage.removeItem(PORTAL_KEYS.PATIENT_NAME);
    storage.removeItem(PORTAL_KEYS.EXPIRES_AT);
  },

  hasSession(): boolean {
    return !!storage.getItem(PORTAL_KEYS.TOKEN) && !this.isExpired();
  },
};

export default {
  setAccessToken,
  getAccessToken,
  setRefreshToken,
  getRefreshToken,
  setUserData,
  getUserData,
  clearAuthTokens,
  hasAuthTokens,
  setAuthTokens,
  portalStorage,
};
